/**
 * EngineAuditPanel — Read-only transparency surface for all ~20 engines.
 *
 * Phase 1.3 — Engine Audit Panel (redesign-p1-1.3-engine-audit-panel)
 *
 * Displays per-engine: output·version·freshness·CONTESTED·error·support status
 * Design spec: docs/plans/2026-06-25-redesign-design-spec.md
 *   - Orange (#FF6600) = brand/signal only
 *   - Verified/done = HUELESS (green retired 2026-07-24); amber = stale/draft, red = blocker/contested
 *   - Near-black surfaces (#0A0A0A → #121212 → #161616)
 *   - DM Mono for money/engine IDs, Outfit for UI, DM Serif for headings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Download, RefreshCw, AlertCircle, CheckCircle2, HelpCircle, BarChart3 } from 'lucide-react';
import { DataTable, Column } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { BRAND_NAME } from '@/config/brand';
import { ShapBar, ShapBarItem } from '@/components/primitives/ShapBar';
import { getFeatureImportance, FeatureImportanceResponse } from '@/api/explainabilityApi';
import { AuditIntegrityPanel } from '@/components/audit';
import { isMoneyKey, OUTPUT_KEY_PRIORITY } from '@/lib/engineOutputKeys';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { api } from '@/utils/api';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface EngineProvenance {
  method_name: string;
  version: string;
}

interface EngineAuditEntry {
  engine_id: string;
  output_value: Record<string, unknown> | null;
  method: EngineProvenance;
  computed_at: string | null;
  freshness_status: 'FRESH' | 'STALE';
  error_state: string | null;
  support_status: 'CONTESTED' | 'SUPPORTED_CONDITIONAL' | 'SUPPORTED';
}

interface EngineAuditData {
  report_id: string;
  engines: EngineAuditEntry[];
  missing: string[];
  generated_at: string;
}

// -----------------------------------------------------------------------------
// Status Badge Component (Design Spec §3)
// -----------------------------------------------------------------------------

type StatusVariant = 'fresh' | 'stale' | 'contested' | 'conditional' | 'supported' | 'error' | 'neutral';

interface StatusBadgeProps {
  text: string;
  variant: StatusVariant;
  className?: string;
}

function StatusBadge({ text, variant, className }: StatusBadgeProps) {
  const variantStyles: Record<StatusVariant, string> = {
    // FRESH = verified/done → HUELESS neutral (green retired 2026-07-24; done carries no hue)
    fresh: 'bg-[var(--color-bg-elevated)] text-[var(--color-verified)] border-[var(--color-border)]',
    // STALE = attention → amber (the draft/stale lane, distinct from the orange accent)
    stale: 'bg-[rgba(217,146,43,0.12)] text-[var(--color-draft)] border-[rgba(217,146,43,0.30)]',
    // CONTESTED = disputed figure → the one blocker red (NOT the orange accent)
    contested: 'bg-[rgba(229,72,77,0.12)] text-[var(--color-blocker)] border-[rgba(229,72,77,0.30)]',
    // CONDITIONAL = gray neutral (token-based so it inverts under Daylight; mirrors the
    // neutral fill of fresh/supported but keeps muted text to read as "conditional", not "done")
    conditional: 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] border-[var(--color-border)]',
    // SUPPORTED = verified/done → HUELESS neutral
    supported: 'bg-[var(--color-bg-elevated)] text-[var(--color-verified)] border-[var(--color-border)]',
    // ERROR = blocker red
    error: 'bg-[rgba(229,72,77,0.12)] text-[var(--color-blocker)] border-[rgba(229,72,77,0.30)]',
    // NEUTRAL = gray (token-based so it inverts under Daylight; text-label/border-sub are the
    // exact light/dark equivalents of the former #4A4A4A/#1E1E1E hardcodes)
    neutral: 'bg-[var(--color-bg-elevated)] text-[var(--color-text-label)] border-[var(--color-border-sub)]',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-none border px-2.5 py-1 text-xs font-semibold',
        variantStyles[variant] || variantStyles.neutral,
        className
      )}
    >
      {variant === 'fresh' && <CheckCircle2 className="h-3 w-3" />}
      {variant === 'stale' && <RefreshCw className="h-3 w-3" />}
      {variant === 'contested' && <AlertCircle className="h-3 w-3" />}
      {variant === 'error' && <AlertCircle className="h-3 w-3" />}
      {variant === 'conditional' && <HelpCircle className="h-3 w-3" />}
      {text}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Monospace Value Display
// -----------------------------------------------------------------------------

interface MonoValueProps {
  children: React.ReactNode;
  className?: string;
}

function MonoValue({ children, className }: MonoValueProps) {
  return (
    <span
      className={cn(
        'font-mono text-sm tabular-nums text-[var(--color-text-secondary)]',
        className
      )}
    >
      {children}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Output Value Renderer
// -----------------------------------------------------------------------------

function OutputValueRenderer({ value }: { value: Record<string, unknown> | null }) {
  if (!value) {
    return <MonoValue className="text-[var(--color-text-ghost)]">—</MonoValue>;
  }

  // Try to find a meaningful value to display
  const keyPriority = OUTPUT_KEY_PRIORITY;

  for (const key of keyPriority) {
    if (value[key] !== undefined && value[key] !== null) {
      const v = value[key];
      if (typeof v === 'number') {
        // Only money keys get "$"; scores / counts are unit-less plain numbers.
        return isMoneyKey(key) ? <FinancialValue value={v} /> : <MonoValue>{v}</MonoValue>;
      }
      if (typeof v === 'string') {
        return <MonoValue>{v}</MonoValue>;
      }
      if (typeof v === 'object') {
        return <MonoValue>{JSON.stringify(v).slice(0, 40)}...</MonoValue>;
      }
    }
  }

  // Fallback: show key count
  const keys = Object.keys(value);
  return <MonoValue className="text-[var(--color-text-muted)]">{keys.length} {keys.length === 1 ? 'field' : 'fields'}</MonoValue>;
}

// -----------------------------------------------------------------------------
// Provenance Renderer
// -----------------------------------------------------------------------------

function ProvenanceRenderer({ method }: { method: EngineProvenance }) {
  return (
    <div className="flex flex-col gap-0.5">
      <MonoValue className="text-xs text-[var(--color-text-secondary)]">
        {method.method_name}
      </MonoValue>
      <MonoValue className="text-xs text-[var(--color-text-muted)]">
        v{method.version}
      </MonoValue>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

interface EngineAuditPanelProps {
  reportId: string;
  className?: string;
}

export function EngineAuditPanel({ reportId, className }: EngineAuditPanelProps) {
  const [data, setData] = useState<EngineAuditData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Feature importance state
  const [featureData, setFeatureData] = useState<FeatureImportanceResponse | null>(null);
  const [featureLoading, setFeatureLoading] = useState(false);
  const [featureError, setFeatureError] = useState<string | null>(null);
  const [showFeatureImportance, setShowFeatureImportance] = useState(false);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<EngineAuditData>(
        `/api/v1/attorney-tools/engine-audit/${reportId}`,
        { showErrorToast: false }
      );
      if (!result || typeof result !== 'object' || Array.isArray(result)) {
        throw new Error('Invalid response from engine audit endpoint');
      }
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  // Fetch global feature importance for SHAP explainability
  const fetchFeatureImportance = useCallback(async () => {
    setFeatureLoading(true);
    setFeatureError(null);
    try {
      const response = await getFeatureImportance();
      setFeatureData(response);
    } catch (err) {
      setFeatureError(err instanceof Error ? err.message : 'Failed to load feature importance');
    } finally {
      setFeatureLoading(false);
    }
  }, []);

  // Load feature importance when expanded
  useEffect(() => {
    if (showFeatureImportance && !featureData && !featureLoading) {
      fetchFeatureImportance();
    }
  }, [showFeatureImportance, featureData, featureLoading, fetchFeatureImportance]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const handleExport = useCallback(() => {
    if (!data) return;

    const exportData = {
      ...data,
      exported_from: BRAND_NAME,
      export_timestamp: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `engine-audit-${reportId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [data, reportId]);

  // Table columns definition
  const columns: Column<EngineAuditEntry>[] = [
    {
      key: 'engine_id',
      label: 'Engine',
      sortable: true,
      width: '180px',
      render: (value: string) => (
        <MonoValue className="font-medium text-[var(--color-text-primary)]">
          {value}
        </MonoValue>
      ),
    },
    {
      key: 'output_value',
      label: 'Output',
      sortable: false,
      width: '200px',
      render: (value: Record<string, unknown> | null) => <OutputValueRenderer value={value} />,
    },
    {
      key: 'method',
      label: 'Provenance',
      sortable: false,
      width: '160px',
      render: (value: EngineProvenance) => <ProvenanceRenderer method={value} />,
    },
    {
      key: 'freshness_status',
      label: 'Freshness',
      sortable: true,
      width: '120px',
      render: (value: 'FRESH' | 'STALE') => (
        <StatusBadge
          text={value}
          variant={value === 'FRESH' ? 'fresh' : 'stale'}
        />
      ),
    },
    {
      key: 'support_status',
      label: 'Support',
      sortable: true,
      width: '140px',
      render: (value: 'CONTESTED' | 'SUPPORTED_CONDITIONAL' | 'SUPPORTED') => {
        const variantMap: Record<string, StatusVariant> = {
          CONTESTED: 'contested',
          SUPPORTED_CONDITIONAL: 'conditional',
          SUPPORTED: 'supported',
        };
        return (
          <StatusBadge
            text={value === 'SUPPORTED_CONDITIONAL' ? 'CONDITIONAL' : value}
            variant={variantMap[value] || 'neutral'}
          />
        );
      },
    },
    {
      key: 'error_state',
      label: 'Status',
      sortable: false,
      width: '120px',
      render: (value: string | null, row: EngineAuditEntry) => {
        if (value) {
          return <StatusBadge text="Error" variant="error" />;
        }
        if (row.freshness_status === 'STALE') {
          return <StatusBadge text="Re-run" variant="stale" />;
        }
        return <StatusBadge text="OK" variant="supported" />;
      },
    },
  ];

  // Loading state
  if (loading) {
    return (
      <div
        className={cn(
          'border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6',
          className
        )}
      >
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-[var(--color-brand)]" />
          <span className="ml-3 text-sm text-[var(--color-text-secondary)]">
            Loading engine audit...
          </span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className={cn(
          'border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6',
          className
        )}
      >
        <div className="flex items-center gap-3 bg-[rgba(255,102,0,0.08)] p-4">
          <AlertCircle className="h-5 w-5 text-[var(--color-brand)]" />
          <div>
            <p className="text-sm font-medium text-[var(--color-text-primary)]">
              Failed to load engine audit
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">{error}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAudit}
            className="ml-auto border-[var(--color-brand)] text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-[var(--color-on-brand)]"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Empty / degraded state. Sub-field guard: a partial payload leaves
  // data.engines undefined and the raw .length crashed /engine-audit/:id to the
  // global ErrorBoundary (2026-07-28 page-sweep). !data.engines also treats a
  // degraded payload as the (already styled) empty state, not a crash.
  if (!data || !data.engines || data.engines.length === 0) {
    return (
      <div
        className={cn(
          'border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6',
          className
        )}
      >
        <div className="py-12 text-center">
          <HelpCircle className="mx-auto h-8 w-8 text-[var(--color-text-ghost)]" />
          <p className="mt-3 text-sm text-[var(--color-text-muted)]">
            No engine outputs found for this case.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'border border-[var(--color-border)] bg-[var(--color-bg-surface)]',
        className
      )}
      style={{ backgroundColor: 'var(--color-bg-surface)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--color-border-sub)] px-5 py-4">
        <div>
          <h2 className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-label)]">
            Engine Audit
          </h2>
          <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
            {data.engines.length} engines • Generated {new Date(data.generated_at).toLocaleString()}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          className="gap-2 border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]"
        >
          <Download className="h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Missing engines notice — guard data.missing too (another sub-field
          that is undefined on a degraded payload); the gate makes it present
          inside the block. */}
      {data.missing && data.missing.length > 0 && (
        <div className="border-b border-[var(--color-border-sub)] bg-[rgba(255,102,0,0.04)] px-5 py-2">
          <p className="text-xs text-[var(--color-text-muted)]">
            <span className="font-medium text-[var(--color-brand)]">{data.missing.length}</span> engine{data.missing.length === 1 ? '' : 's'}{' '}
            not yet run:{' '}
            <span className="font-mono text-[var(--color-text-dim)]">
              {data.missing.join(', ')}
            </span>
          </p>
        </div>
      )}

      {/* Data Table */}
      <div className="p-0">
        <DataTable
          columns={columns}
          data={data.engines}
          emptyMessage="No engine data available"
          className="border-0"
        />
      </div>

      {/* Feature Importance Section */}
      <div className="border-t border-[var(--color-border-sub)]">
        <button
          onClick={() => setShowFeatureImportance(!showFeatureImportance)}
          className="w-full flex items-center justify-between px-5 py-3 hover:bg-[var(--color-bg-hover)] transition-colors"
        >
          <div className="flex items-center gap-3">
            <BarChart3 className="h-4 w-4 text-[var(--color-text-muted)]" />
            <span
              className="text-sm font-medium"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Feature Importance
            </span>
            <span className="text-xs text-[var(--color-text-muted)]">
              — SHAP global explainability
            </span>
          </div>
          <div className="flex items-center gap-2">
            {showFeatureImportance ? (
              <span className="text-xs text-[var(--color-text-muted)]">Hide</span>
            ) : (
              <span className="text-xs text-[var(--color-text-secondary)]">Show ▾</span>
            )}
          </div>
        </button>

        {showFeatureImportance && (
          <div className="px-5 pb-5">
            {featureLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-5 w-5 animate-spin text-[var(--color-brand)] mr-2" />
                <span className="text-sm text-[var(--color-text-muted)]">
                  Loading feature importance...
                </span>
              </div>
            ) : featureError ? (
              <div className="bg-[rgba(255,102,0,0.08)] p-4 flex items-center gap-3">
                <AlertCircle className="h-4 w-4 text-[var(--color-brand)]" />
                <div className="flex-1">
                  <p className="text-sm text-[var(--color-text-primary)]">
                    Failed to load feature importance
                  </p>
                  <p className="text-xs text-[var(--color-text-muted)]">{featureError}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchFeatureImportance}
                  className="border-[var(--color-brand)] text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-[var(--color-on-brand)]"
                >
                  Retry
                </Button>
              </div>
            ) : featureData && featureData.features.length > 0 ? (
              <ShapBar
                items={featureData.features.map((f) => ({
                  feature: f.feature,
                  attribution: f.importance,
                  description: f.description,
                  isMonetary: f.is_monetary,
                }))}
                maxItems={10}
              />
            ) : (
              <div className="text-center py-6">
                <HelpCircle className="mx-auto h-6 w-6 text-[var(--color-text-ghost)] mb-2" />
                <p className="text-sm text-[var(--color-text-muted)]">
                  No feature importance data available
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Audit Integrity Section */}
      <div className="border-t border-[var(--color-border-sub)]">
        <AuditIntegrityPanel reportId={reportId} />
      </div>
    </div>
  );
}

export default EngineAuditPanel;
